import { ProjectLinkCreateDialog } from '@/components/ProjectLinkCreateDialog';
import { ProjectLinkItem } from '@/components/ProjectLinkItem';
import { ProjectLinkPasswordDialog } from '@/components/ProjectLinkPasswordDialog';
import { Button } from '@/components/ui/button';
import { DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { execute } from '@/graphql/execute';
import { deleteProjectLinkMutation } from '@/mutations/project.mutation';
import { useDialog } from '@/providers/DialogProvider';
import { getProjectLinksQuery } from '@/queries/project.query';
import { Plus } from '@mynaui/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { analytics } from '@/lib/analytics';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

interface ProjectShareDialogContentProps {
  projectId: string;
}

export function ProjectShareDialogContent({ projectId }: ProjectShareDialogContentProps) {
  const { t } = useTranslation('components');
  const { openDialog, dismissActiveDialog } = useDialog();
  const queryClient = useQueryClient();

  // Fetch existing project links
  const { data: projectLinks, isLoading } = useQuery({
    queryKey: ['projectLinks', projectId],
    queryFn: () => execute(getProjectLinksQuery, { projectId }),
    enabled: !!projectId,
  });

  const deleteLinkMutation = useMutation({
    mutationFn: async (linkId: string) => {
      return execute(deleteProjectLinkMutation, { id: linkId });
    },
    onSuccess: () => {
      analytics.capture('Project Link Deleted');
      queryClient.invalidateQueries({ queryKey: ['projectLinks', projectId] });
      queryClient.invalidateQueries({ queryKey: ['allProjectLinks'] });
      toast.success(t('shareDialog.linkDeleted'));
    },
    onError: (error: any) => {
      toast.error(error.message || t('shareDialog.failedDeleteLink'));
    },
  });

  const _handleDeleteLink = async (linkId: string) => {
    if (confirm(t('shareDialog.confirmDeleteLink'))) {
      await deleteLinkMutation.mutateAsync(linkId);
    }
  };

  const _handleCopyLink = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      analytics.capture('Project Link Copied');
      toast.success(t('shareDialog.linkCopied'));
    } catch (error) {
      toast.error(t('shareDialog.failedCopyLink'));
    }
  };

  const _handleCreateNewLink = () => {
    openDialog(<ProjectLinkCreateDialog projectId={projectId} />);
  };

  const _handleEditPassword = (link: any) => {
    openDialog(
      <ProjectLinkPasswordDialog
        linkId={link.id}
        linkName={link.name || t('shareDialog.untitled')}
        hasPassword={link.hasPassword}
        projectId={projectId}
      />,
    );
  };

  const isLinkExpired = (expiresAt: string) => {
    return dayjs(expiresAt).isBefore(dayjs());
  };

  const _handleClose = () => {
    dismissActiveDialog();
  };

  const activeLinks = projectLinks?.projectLinks?.filter((link: any) => link.status === 'ACTIVE');
  const expiredLinks = projectLinks?.projectLinks?.filter((link: any) => link.status === 'EXPIRED');

  return (
    <>
      <DialogHeader id='onboarding-project-sharing'>
        <DialogTitle>{t('shareDialog.title')}</DialogTitle>
      </DialogHeader>

      <div className='space-y-6'>
        {/* Create new link button */}
        <div className='flex justify-between items-center'>
          <div>
            <h3 className='font-semibold text-slate-900 dark:text-stone-200'>{t('shareDialog.projectLinks')}</h3>
            <p className='text-xs font-medium text-slate-500 dark:text-stone-200/70'>
              {t('shareDialog.manageLinks')}
            </p>
          </div>
          <Button onClick={_handleCreateNewLink} size='icon' className='size-7 p-0'>
            <Plus className='size-5 stroke-2' />
          </Button>
        </div>

        {/* Existing links list */}
        <div className='space-y-4'>
          {isLoading ? (
            <div className='text-sm text-muted-foreground'>{t('shareDialog.loadingLinks')}</div>
          ) : projectLinks?.projectLinks?.length === 0 ? (
            <div className='text-sm text-muted-foreground'>{t('shareDialog.noLinks')}</div>
          ) : (
            <div className='flex flex-col gap-2'>
              {activeLinks?.map((link: any) => (
                <ProjectLinkItem
                  key={link.id}
                  link={link}
                  projectId={projectId}
                  onCopy={_handleCopyLink}
                  onDelete={_handleDeleteLink}
                  onEditPassword={_handleEditPassword}
                  isLinkExpired={isLinkExpired}
                  deletePending={deleteLinkMutation.isPending}
                />
              ))}
              {!!expiredLinks?.length && (
                <div className='text-sm font-medium text-slate-500 dark:text-stone-200/70 mt-4 flex items-center justify-between'>
                  <span>{t('shareDialog.expiredLinks')}</span>
                </div>
              )}
              {expiredLinks?.map((link: any) => (
                <ProjectLinkItem
                  key={link.id}
                  link={link}
                  projectId={projectId}
                  onCopy={_handleCopyLink}
                  onDelete={_handleDeleteLink}
                  onEditPassword={_handleEditPassword}
                  isLinkExpired={isLinkExpired}
                  deletePending={deleteLinkMutation.isPending}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <DialogFooter>
        <Button variant='outline' onClick={_handleClose}>
          {t('shareDialog.close')}
        </Button>
      </DialogFooter>
    </>
  );
}
